import { memoize, merge } from 'lodash';
import { z } from 'zod/v3';

/**
 * Runtime config for UI apps that should be attached to the global `window` object (e.g. by an `/app-config.js` script
 * in the HTML) and available when the application starts (similar to boot flags). Unlike boot flags (which are
 * backed by feature flag values), values here usually come from `envconfig` and will only change if the app gets
 * re-deployed.
 */
export const getAppConfig = memoize(_getAppConfig);

// Function above uses memoize on this so that we don't repeatedly log errors / do validation on the values every time
// someone asks for them (they shouldn't change once someone loads the app)
function _getAppConfig(): AppConfig {
  try {
    const appConfig = AppConfig.parse(window.chronoAppConfig);
    return appConfig;
  } catch (err) {
    console.error('Failed to get AppConfig from window object, using default values', err);
  }

  // Return default values in case of failure
  return getAppConfigDefaults();
}

/**
 * Get the default values used in case of failure retrieving / validating the actual values. Exported for tests.
 */
export function getAppConfigDefaults() {
  return {
    // Use hostname's subdomain by default even though this won't be accurate when, for example, running against a
    // localhost proxy
    tenantNamespace: window.location.hostname.split('.')[0] ?? 'unknown',
    defaultRetention: '120h',
    enableExperimentalDashboardFeatures: false,
    enableAllVizFrameworkPlugins: false,
    enableChronosphereChangeEventLinks: false,
  };
}

// Helper schema for strings that can't be empty
const nonEmptyString = z.string().min(1, { message: 'String cannot be empty' });

/**
 * Zod schema for validating the AppConfig object attached to the `window` object.
 */
const AppConfig = z.object({
  tenantNamespace: nonEmptyString,
  defaultRetention: nonEmptyString,
  enableExperimentalDashboardFeatures: z.boolean(),
  enableAllVizFrameworkPlugins: z.boolean(),
  enableChronosphereChangeEventLinks: z.boolean(),
});

export type AppConfig = z.infer<typeof AppConfig>;

declare global {
  interface Window {
    chronoAppConfig: unknown;
  }
}

/**
 * Helper to set AppConfig on the global window object to the values provided in tests. Default values are used for
 * missing values on `config` (or if `config` is `undefined`);
 */
export function setAppConfig(config?: Partial<AppConfig>) {
  if (process.env.NODE_ENV !== 'production') {
    const values = merge({}, getAppConfigDefaults(), config);
    window.chronoAppConfig = values;
  }
}
